import { useEffect, useState } from "react";
import { Loader2, ShieldCheck, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";

const APPLE_ID = process.env.REACT_APP_APPLE_CLIENT_ID || "";

function SocialButtons({ intent = "login" }) {
  const { loginGoogle, loginApple } = useAuth();

  useEffect(() => {
    if (APPLE_ID && window.AppleID) {
      try {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        window.AppleID.auth.init({
          clientId: APPLE_ID,
          scope: "name email",
          redirectURI: window.location.origin + "/",
          usePopup: true,
        });
      } catch {
        /* ignore */
      }
    }
  }, []);

  const onApple = async () => {
    if (!APPLE_ID || !window.AppleID) {
      toast.message("Apple Sign In is ready and will be enabled on the live domain.");
      return;
    }
    try {
      const data = await window.AppleID.auth.signIn();
      await loginApple({
        identity_token: data.authorization.id_token,
        authorization_code: data.authorization.code,
        name: data.user?.name ? `${data.user.name.firstName} ${data.user.name.lastName}` : undefined,
        email: data.user?.email,
      });
      toast.success("Signed in with Apple");
    } catch {
      toast.error("Apple Sign In was cancelled or unavailable on this domain");
    }
  };

  return (
    <div className="grid grid-cols-2 gap-3">
      <button
        type="button"
        onClick={() => loginGoogle(intent)}
        data-testid="auth-google"
        className="inline-flex items-center justify-center gap-2 rounded-xl border border-border bg-card/50 px-4 py-2.5 text-sm font-semibold transition-colors hover:border-gold/40"
      >
        <svg viewBox="0 0 24 24" className="h-4 w-4"><path fill="#EA4335" d="M12 10.2v3.9h5.5c-.2 1.4-1.6 4.1-5.5 4.1-3.3 0-6-2.7-6-6s2.7-6 6-6c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.9 3.1 14.7 2 12 2 6.9 2 2.8 6.1 2.8 11.2S6.9 20.4 12 20.4c5.9 0 9.8-4.1 9.8-9.9 0-.7 0-1.2-.2-1.7H12z"/></svg>
        Google
      </button>
      <button
        type="button"
        onClick={onApple}
        data-testid="auth-apple"
        className="inline-flex items-center justify-center gap-2 rounded-xl border border-border bg-card/50 px-4 py-2.5 text-sm font-semibold transition-colors hover:border-gold/40"
      >
        <svg viewBox="0 0 384 512" className="h-4 w-4" fill="currentColor"><path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zM262.1 104.5c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/></svg>
        Apple
      </button>
    </div>
  );
}

const Divider = () => (
  <div className="flex items-center gap-3 py-1 text-xs text-muted-foreground">
    <span className="h-px flex-1 bg-border" /> or <span className="h-px flex-1 bg-border" />
  </div>
);

export default function AuthDialog() {
  const { dialog, closeAuth, login, register, verifyEmail, verify2fa, resendOtp, send2faRecovery, completeAge } = useAuth();
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState("form"); // form | verify | age
  const [challenge, setChallenge] = useState("email"); // email | 2fa
  const [preToken, setPreToken] = useState("");
  const [pendingEmail, setPendingEmail] = useState("");
  const [authTab, setAuthTab] = useState("login");
  const [notice, setNotice] = useState("");
  const [prefillEmail, setPrefillEmail] = useState("");
  const [signupFlow, setSignupFlow] = useState(false);
  const [dob, setDob] = useState("");

  useEffect(() => {
    if (dialog.open) {
      setStep(dialog.step || "form");
      setChallenge(dialog.challenge || "email");
      setPreToken(dialog.preToken || "");
      setPendingEmail(dialog.email || "");
      setAuthTab(dialog.mode === "signup" ? "signup" : "login");
      setNotice(dialog.notice || "");
      setPrefillEmail(dialog.notice ? (dialog.email || "") : "");
      setSignupFlow(false);
      setDob("");
      setBusy(false);
    }
  }, [dialog.open, dialog.mode, dialog.step, dialog.challenge, dialog.preToken, dialog.email, dialog.notice]);

  const today = new Date().toISOString().split("T")[0];
  const ageFromDob = (d) => {
    if (!d) return null;
    const b = new Date(d);
    if (Number.isNaN(b.getTime())) return null;
    const now = new Date();
    let a = now.getFullYear() - b.getFullYear();
    const m = now.getMonth() - b.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < b.getDate())) a -= 1;
    return a;
  };
  const age = ageFromDob(dob);
  const isMinor = age !== null && age >= 13 && age <= 17;
  const isTooYoung = age !== null && age < 13;

  const finish = () => {
    toast.success("You're signed in.");
    const after = dialog.after;
    closeAuth();
    if (typeof after === "function") setTimeout(after, 150);
  };

  const onLogin = async (e) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const email = f.get("email").trim();
    setBusy(true);
    setSignupFlow(false);
    try {
      const d = await login({ email, password: f.get("password") });
      if (d.token) {
        finish();
      } else if (d.needs_2fa) {
        setChallenge("2fa");
        setPreToken(d.pre_token);
        setPendingEmail(d.email || email);
        setStep("verify");
        toast.message("Enter your two-factor code to continue.");
      } else if (d.needs_verification) {
        setChallenge("email");
        setPreToken(d.pre_token);
        setPendingEmail(d.email || email);
        setStep("verify");
        toast.message("Please verify your email — we sent you a code.");
      } else {
        toast.error("Could not sign you in. Please try again.");
      }
    } catch (err) {
      toast.error(err.message || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const onSignup = async (e) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const email = f.get("email").trim();
    setBusy(true);
    try {
      const d = await register({
        name: f.get("name").trim(),
        email,
        password: f.get("password"),
      });
      setChallenge("email");
      setPreToken(d.pre_token);
      setPendingEmail(d.email || email);
      setSignupFlow(true);
      setStep("verify");
      toast.message("Check your email for the verification code.");
    } catch (err) {
      const msg = err.message || "Sign up failed";
      if (/already\s*(exists|registered|in use|available)/i.test(msg)) {
        setPrefillEmail(email);
        setNotice("An account with this email already exists. Please log in instead.");
        setAuthTab("login");
        toast.error("This account already exists — please log in instead.");
      } else {
        toast.error(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  const onVerify = async (e) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    setBusy(true);
    try {
      if (challenge === "2fa") {
        await verify2fa({ pre_token: preToken, code: f.get("code").trim(), trust_device: f.get("trust") === "on" });
        finish();
      } else {
        await verifyEmail({ pre_token: preToken, code: f.get("code").trim() });
        if (signupFlow) {
          setStep("age");
        } else {
          finish();
        }
      }
    } catch (err) {
      toast.error(err.message || "Invalid code");
    } finally {
      setBusy(false);
    }
  };

  const onCompleteAge = async (e) => {
    e.preventDefault();
    if (isTooYoung) {
      toast.error("You must be at least 13 to use Warranty_Vault pro.");
      return;
    }
    const f = new FormData(e.currentTarget);
    setBusy(true);
    try {
      await completeAge({
        date_of_birth: dob,
        guardian_email: isMinor ? f.get("guardian_email").trim() : undefined,
        guardian_consent: isMinor ? f.get("guardian_consent") === "on" : undefined,
        marketing_opt_in: f.get("marketing_opt_in") === "on",
      });
      finish();
    } catch (err) {
      toast.error(err.message || "Could not save your details");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={dialog.open} onOpenChange={(o) => (o ? null : closeAuth())}>
      <DialogContent className="max-w-md border-border bg-background" data-testid="auth-dialog">
        {step === "verify" ? (
          <>
            <DialogHeader>
              <button onClick={() => setStep("form")} className="mb-1 inline-flex w-fit items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
                <ArrowLeft className="h-3.5 w-3.5" /> back
              </button>
              <DialogTitle className="font-display text-xl">
                {challenge === "2fa" ? "Two-factor verification" : "Verify your email"}
              </DialogTitle>
              <DialogDescription>
                {challenge === "2fa" ? (
                  <>Enter the code from your authenticator app, or the code we just emailed to <span className="font-medium text-foreground">{pendingEmail}</span>.</>
                ) : (
                  <>Enter the 6-digit code we sent to <span className="font-medium text-foreground">{pendingEmail}</span>.</>
                )}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={onVerify} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="code">{challenge === "2fa" ? "Authentication code" : "Verification code"}</Label>
                <Input id="code" name="code" inputMode="numeric" autoComplete="one-time-code" placeholder="123456" data-testid="auth-otp" required />
              </div>
              {challenge === "2fa" && (
                <label className="flex items-center gap-2 text-sm text-muted-foreground">
                  <input type="checkbox" name="trust" className="h-4 w-4 rounded border-border accent-[hsl(var(--primary))]" data-testid="auth-trust-device" />
                  Trust this device for 30 days
                </label>
              )}
              <button type="submit" disabled={busy} data-testid="auth-verify-submit" className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 font-semibold text-primary-foreground disabled:opacity-60">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Verify & continue
              </button>
              {challenge === "email" && (
                <button
                  type="button"
                  onClick={async () => { try { await resendOtp(preToken); toast.success("Code resent"); } catch { toast.error("Could not resend"); } }}
                  className="w-full text-center text-xs text-muted-foreground hover:text-gold"
                >
                  Resend code
                </button>
              )}
              {challenge === "2fa" && (
                <button
                  type="button"
                  data-testid="auth-2fa-recovery"
                  onClick={async () => { try { await send2faRecovery(preToken); toast.success("We've emailed you a recovery code to sign in."); } catch (err) { toast.error(err.message || "Could not send recovery code"); } }}
                  className="w-full text-center text-xs text-muted-foreground hover:text-gold"
                >
                  Can't use your authenticator? Email me a recovery code
                </button>
              )}
            </form>
          </>
        ) : step === "age" ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-xl">One last step</DialogTitle>
              <DialogDescription>
                Confirm your date of birth to finish setting up your Warranty_Vault pro account.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={onCompleteAge} className="space-y-4" data-testid="age-form">
              <div className="space-y-2">
                <Label htmlFor="su-dob">Date of birth</Label>
                <Input
                  id="su-dob"
                  name="date_of_birth"
                  type="date"
                  value={dob}
                  max={today}
                  onChange={(e) => setDob(e.target.value)}
                  data-testid="signup-dob"
                  required
                />
              </div>
              {isTooYoung && (
                <div data-testid="age-too-young" className="rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-foreground/90">
                  Sorry — Warranty_Vault pro isn't available to anyone under 13.
                </div>
              )}
              {isMinor && (
                <>
                  <div data-testid="age-guardian-note" className="rounded-xl border border-gold/40 bg-gold/10 px-4 py-3 text-sm text-foreground/90">
                    Because you're under 18, we need a parent or guardian's consent to continue.
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="su-guardian">Parent/guardian email</Label>
                    <Input id="su-guardian" name="guardian_email" type="email" placeholder="guardian@example.com" data-testid="signup-guardian-email" required />
                  </div>
                  <label className="flex items-start gap-2 text-sm text-muted-foreground">
                    <input type="checkbox" name="guardian_consent" className="mt-0.5 h-4 w-4 rounded border-border accent-[hsl(var(--primary))]" data-testid="signup-guardian-consent" required />
                    I confirm my parent or guardian consents to me using Warranty_Vault pro.
                  </label>
                </>
              )}
              <label className="flex items-start gap-2 text-sm text-muted-foreground">
                <input type="checkbox" name="marketing_opt_in" className="mt-0.5 h-4 w-4 rounded border-border accent-[hsl(var(--primary))]" data-testid="signup-marketing-optin" />
                Send me occasional product news and tips (optional).
              </label>
              <button type="submit" disabled={busy || isTooYoung || !dob} data-testid="age-submit" className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 font-semibold text-primary-foreground disabled:opacity-60">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Finish & continue
              </button>
            </form>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-xl">Welcome to Warranty_Vault pro</DialogTitle>
              <DialogDescription>
                Use the same account on the website and the app — sign up here and log straight into the app.
              </DialogDescription>
            </DialogHeader>

            <Tabs value={authTab} onValueChange={(v) => { setAuthTab(v); if (v === "signup") setNotice(""); }} className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="login" data-testid="auth-tab-login">Log in</TabsTrigger>
                <TabsTrigger value="signup" data-testid="auth-tab-signup">Sign up</TabsTrigger>
              </TabsList>

              <TabsContent value="login" className="mt-4">
                {notice && (
                  <div data-testid="auth-login-notice" className="mb-4 rounded-xl border border-gold/40 bg-gold/10 px-4 py-3 text-sm text-foreground/90">
                    {notice}
                  </div>
                )}
                <form onSubmit={onLogin} className="space-y-4" data-testid="login-form">
                  <div className="space-y-2">
                    <Label htmlFor="li-email">Email</Label>
                    <Input key={prefillEmail} id="li-email" name="email" type="email" autoComplete="email" placeholder="you@example.com" defaultValue={prefillEmail} data-testid="login-email" required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="li-pass">Password</Label>
                    <Input id="li-pass" name="password" type="password" autoComplete="current-password" placeholder="••••••••" data-testid="login-password" required />
                  </div>
                  <button type="submit" disabled={busy} data-testid="login-submit" className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 font-semibold text-primary-foreground disabled:opacity-60">
                    {busy && <Loader2 className="h-4 w-4 animate-spin" />} Log in
                  </button>
                </form>
              </TabsContent>

              <TabsContent value="signup" className="mt-4">
                <form onSubmit={onSignup} className="space-y-4" data-testid="signup-form">
                  <div className="space-y-2">
                    <Label htmlFor="su-name">Name</Label>
                    <Input id="su-name" name="name" autoComplete="name" placeholder="Your name" data-testid="signup-name" required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="su-email">Email</Label>
                    <Input id="su-email" name="email" type="email" autoComplete="email" placeholder="you@example.com" data-testid="signup-email" required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="su-pass">Password</Label>
                    <Input id="su-pass" name="password" type="password" autoComplete="new-password" placeholder="Create a password" data-testid="signup-password" required />
                  </div>
                  <button type="submit" disabled={busy} data-testid="signup-submit" className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 font-semibold text-primary-foreground disabled:opacity-60">
                    {busy && <Loader2 className="h-4 w-4 animate-spin" />} Create account
                  </button>
                </form>
              </TabsContent>
            </Tabs>

            <Divider />
            <SocialButtons intent={authTab} />
            <p className="flex items-center justify-center gap-1.5 text-center text-xs text-muted-foreground">
              <ShieldCheck className="h-3.5 w-3.5 text-sage" /> Secured by the same system as the Warranty_Vault pro app
            </p>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
